import { ref } from 'vue';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { captureError } from '@/sentry';
import { version } from '../../../../../package.json';
import gt from 'semver/functions/gt';

import NativeAppConfig from '@/config/native-app.config.json';
import useQueryCacheHelper from '@/shared/composables/useQueryCacheHelper';
import { Preferences } from '@capacitor/preferences';

const PENDING_UPDATE_KEY = 'pendingUpdate';
const isDownloading = ref(false);

export const useSoftUpdate = () => {
	const latestVersion = ref();
	const { removeCaches, removeCachesForSoftUpdate } = useQueryCacheHelper();

	const checkForUpdates = async () => {
		try {
			if (isDownloading.value) return;

			const id = `clientx-2.0-${NativeAppConfig.CAP_SOFT_UPDATE_CHANNEL ?? process.env.VUE_APP_ENVIRONMENT_CAP_UPDATE_CHANNEL ?? 'production-mc'}`;
			CapacitorUpdater.setCustomId({
				customId: id,
			});
			latestVersion.value = await CapacitorUpdater.getLatest();
			if (latestVersion.value?.url && gt(latestVersion.value.version, version)) {
				console.log('[SilentUpdate] New version available, downloading...', { latestVersion: latestVersion.value.version, version });
				await downloadUpdate(latestVersion.value);
			}
		} catch (e: unknown) {
			console.log('[SilentUpdate] Failed to check for updates.', e);
			captureError(e);
		}
	};

	const downloadUpdate = async (updateInfo: any) => {
		if (isDownloading.value) return;
		isDownloading.value = true;

		try {
			const appUpdateData = await CapacitorUpdater.download({
				url: updateInfo.url,
				version: updateInfo.version,
			});
			await Preferences.set({ key: PENDING_UPDATE_KEY, value: String(appUpdateData.id) });
			console.log('[SilentUpdate] Update downloaded and saved. Will apply on next resume/cold start.');
		} catch (e) {
			console.log('[SilentUpdate] Download failed, will retry on next check.', e);
			captureError(e);
		} finally {
			isDownloading.value = false;
		}
	};

	const applyPendingUpdate = async () => {
		try {
			const { value: pendingId } = await Preferences.get({ key: PENDING_UPDATE_KEY });
			if (pendingId) {
				console.log('[SilentUpdate] Applying pending update...', pendingId);
				removeCaches();
				removeCachesForSoftUpdate();
				CapacitorUpdater.set({ id: pendingId });
			}
		} catch (e) {
			console.log('[SilentUpdate] Failed to apply pending update.', e);
			captureError(e);
		} finally {
			await Preferences.remove({ key: PENDING_UPDATE_KEY });
		}
	};

	return {
		checkForUpdates,
		applyPendingUpdate,
	};
};
